/**
 * Top high-risk events \u2014 default last-24h riskBands=high override
 * applied only when the user has no other filters active. Once any
 * filter is set, the live filters take over so the card shows what the
 * user is investigating.
 */
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { api } from "@/lib/api";
import { useFilters } from "@/hooks/useFilters";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { Card, CardHeader, EmptyState, ErrorState, Skeleton } from "@/components/primitives";
import { MonoValue } from "@/components/primitives/MonoValue";
import { MethodBadge } from "@/components/badges/MethodBadge";
import { RiskBandBadge } from "@/components/badges";
import { ReasonChips } from "@/components/reasons/ReasonChips";
import { IdentityCell } from "@/components/identity/IdentityCell";
import { parseReasons } from "@/lib/reasonCodes";
import { formatRelative, formatTime } from "@/lib/time";
import { truncateMiddle } from "@/lib/format";
import { countActiveFilters } from "@/lib/filterCount";
import { endpointAreaLabel } from "@/config/filters";
import type { FilterState } from "@/types";
import type { EntityTypeV1 } from "@/types/v1";

export function TopHighRiskEventsCard({ onEntity }: { onEntity?: (t: EntityTypeV1, v: string) => void }) {
  const { filters, patchFilters } = useFilters();
  const { tick } = useAutoRefresh();

  const activeCount = countActiveFilters(filters);
  const usingDefault = activeCount === 0;
  const queryFilters: Partial<FilterState> = useMemo(
    () => usingDefault
      ? { ...filters, timeRange: "24h", riskBands: ["high"] }
      : filters,
    [filters, usingDefault],
  );

  const q = useQuery({
    queryKey: ["top-high-risk-events", queryFilters, tick],
    queryFn: ({ signal }) =>
      api.events(queryFilters, { page: 1, pageSize: 20, sort: "score", sortDir: "desc" }, signal),
  });

  const handleEntity = (t: EntityTypeV1, v: string) => {
    if (onEntity) onEntity(t, v);
  };

  const subtitle = usingDefault
    ? "Highest-scoring requests in the last 24h \u2014 set any filter to override"
    : "Top 20 highest-scoring requests matching your active filters";

  return (
    <Card>
      <CardHeader title="Top high-risk events" subtitle={subtitle} />
      {q.isLoading ? (
        <Skeleton className="h-64" />
      ) : q.error ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : !q.data?.data?.rows?.length ? (
        <EmptyState title="No high-risk events in the active window." />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border lg:overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-2 text-[11px] uppercase tracking-wider text-fg-subtle">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Time</th>
                <th className="px-3 py-2 text-left font-medium">Risk</th>
                <th className="px-3 py-2 text-left font-medium">Area</th>
                <th className="px-3 py-2 text-left font-medium">Identity</th>
                <th className="px-3 py-2 text-left font-medium">Source</th>
                <th className="px-3 py-2 text-left font-medium">Reasons</th>
              </tr>
            </thead>
            <tbody>
              {q.data.data.rows.map((r) => (
                <tr key={r.id} className="border-t border-border hover:bg-surface-2">
                  <td className="px-3 py-2 align-top text-fg-muted whitespace-nowrap" title={formatTime(r.ts)}>
                    {formatRelative(r.ts)}
                  </td>
                  <td className="px-3 py-2 align-top">
                    <div className="flex items-center gap-1.5">
                      <span className="font-mono text-fg">{r.score}</span>
                      <RiskBandBadge band={r.band} />
                    </div>
                  </td>
                  <td className="px-3 py-2 align-top">
                    <div className="text-sm font-medium text-fg">{endpointAreaLabel(r.endpointGroup)}</div>
                    <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-[11px] text-fg-subtle">
                      <MethodBadge method={r.method} />
                      <span className="truncate font-mono" title={r.pathBucket || ""}>
                        {r.endpointType ? `${r.endpointType} · ` : ""}{truncateMiddle(r.pathBucket || "\u2014", 32)}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-2 align-top">
                    <IdentityCell row={r} onEntity={handleEntity} />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <MonoValue value={r.ip} onClick={() => handleEntity("ip", r.ip)} />
                    <div className="mt-0.5 text-[10px] text-fg-subtle">
                      {r.country ?? "\u2014"}
                    </div>
                  </td>
                  <td className="px-3 py-2 align-top">
                    <ReasonChips
                      parsed={parseReasons(r.reasons)}
                      contributions={r.categoryContributions}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="border-t border-border bg-surface-2 px-3 py-2 text-xs text-fg-subtle">
            Showing top {q.data.data.rows.length} of {q.data.data.total ?? "?"} matching events.{" "}
            <button
              onClick={() => {
                if (usingDefault) patchFilters({ timeRange: "24h", riskBands: ["high"] });
              }}
              className="underline hover:text-fg"
            >Open in Investigations \u2192</button>
          </div>
        </div>
      )}
    </Card>
  );
}
